<template>
  <div class="bg-gradient-to-b from-primary-100 to-white to-[50vh]">
    <app-page-wrapper class="pt-4">
      <app-button-back
        class="text-primary-950 hover:bg-primary-200"
        :to="previousRoute ?? '/catalog'"
      />
    </app-page-wrapper>

    <app-page-wrapper
      class="grid gap-8 pt-8 md:pt-12 lg:grid-cols-[auto_320px] xl:grid-cols-[auto_360px]"
    >
      <div>
        <teacher-page-header :teacher="teacher" />

        <div
          class="grid gap-16 py-16 md:gap-24"
          :class="teacherPageContentClasses"
        >
          <teacher-page-course-list :teacher="teacher" />

          <teacher-page-description :teacher="teacher" />

          <teacher-page-testimonials
            v-if="teacher.testimonials.length > 0"
            :testimonials="teacher.testimonials"
          />

          <teacher-page-next-sessions-section
            v-if="nextCourses.length > 0"
            :list="nextCourses"
          />

          <teacher-page-related-blog-post-list :teacher="teacher" />
        </div>
      </div>

      <div class="hidden lg:block">
        <div class="sticky-below-header grid gap-8">
          <teacher-page-contact :teacher="teacher" />

          <teacher-page-next-sessions-panel
            v-if="nextCourses.length > 0"
            :list="nextCourses"
          />
        </div>
      </div>
    </app-page-wrapper>

    <div
      v-if="otherTeachers.length > 0"
      class="mt-16 bg-primary-50 py-16 text-primary-950"
    >
      <app-page-wrapper>
        <app-page-subsection
          :title="$t('teacher.page.other_teacher_list_title_default')"
        >
          <teacher-card-carousel :list="otherTeachers" />
        </app-page-subsection>
      </app-page-wrapper>
    </div>

    <teacher-page-mobile-actions
      class="lg:hidden"
      :next-courses="nextCourses"
      :teacher="teacher"
    />
  </div>
</template>

<script lang="ts" setup>
import TeacherPageContact from "~/pages/trainers/[slug]/_includes/components/teacher-page-contact.vue";
import TeacherPageCourseList from "~/pages/trainers/[slug]/_includes/components/teacher-page-course-list.vue";
import TeacherPageDescription from "~/pages/trainers/[slug]/_includes/components/teacher-page-description.vue";
import TeacherPageHeader from "~/pages/trainers/[slug]/_includes/components/teacher-page-header.vue";
import TeacherPageMobileActions from "~/pages/trainers/[slug]/_includes/components/teacher-page-mobile-actions.vue";
import TeacherPageNextSessionsPanel from "~/pages/trainers/[slug]/_includes/components/teacher-page-next-sessions-panel.vue";
import TeacherPageNextSessionsSection from "~/pages/trainers/[slug]/_includes/components/teacher-page-next-sessions-section.vue";
import TeacherPageRelatedBlogPostList from "~/pages/trainers/[slug]/_includes/components/teacher-page-related-blog-post-list.vue";
import TeacherPageTestimonials from "~/pages/trainers/[slug]/_includes/components/teacher-page-testimonials.vue";
import { useTeacherPageData } from "~/pages/trainers/[slug]/_includes/composables/teacher-page.data";
import { useTeacherPageMetaData } from "~/pages/trainers/[slug]/_includes/composables/teacher-page-meta.hook";

const slug = useRoute("trainers-slug").params.slug;

const { teacher } = await useTeacherPageData(slug);
const mainTagSlug = teacher.value.mainTag?.path.split(".")[0];

const { data: otherTeachers } = useTeacherList(
  `teachers_${slug}`,
  {
    limit: 15,
    exclude: [teacher.value.slug!],
    tags: mainTagSlug ? [mainTagSlug] : undefined,
  },
  {
    lazy: true,
    server: false,
  },
);

const { data: nextCourses } = useCourseList(`trainer_${teacher.value.slug}`, {
  teachers: [teacher.value.id],
  from: new Date().toISOString(),
});

useTeacherPageMetaData(teacher);

defineAnalyticsContext({
  teacher: {
    id: teacher.value.id,
    full_name: teacher.value.full_name,
    slug: teacher.value.slug,
  },
});

const teacherPageContentClasses = "mx-auto w-full max-w-[1080px] md:px-8";

const previousRoute = usePreviousRoute();
</script>
